import React, { forwardRef, type HTMLAttributes } from "react";
import { cn } from "../lib/utils";

export interface KpiCardProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  value: string;
  icon: React.ReactNode;
  dense?: boolean;
  trend?: { direction: "up" | "down" | "neutral"; value: string };
  iconVariant?: "accent" | "success" | "warning" | "danger" | "info";
}

const iconBgMap = {
  accent: "bg-accent/10 text-accent",
  success: "bg-success-bg text-success",
  warning: "bg-warning-bg text-warning",
  danger: "bg-error-bg text-error",
  info: "bg-accent/10 text-accent",
};

const KpiCard = forwardRef<HTMLDivElement, KpiCardProps>(
  ({ className, label, value, icon, dense = false, trend, iconVariant = "accent", ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center justify-between rounded-lg border border-border bg-card p-6 transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md",
        dense && "p-4",
        className,
      )}
      {...props}
    >
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className={cn("font-bold text-foreground tabular-nums", dense ? "text-lg" : "text-2xl")}>{value}</span>
        {trend && (
          <span className={cn("mt-1 inline-flex items-center gap-0.5 text-xs font-medium", trend.direction === "up" && "text-success", trend.direction === "down" && "text-destructive", trend.direction === "neutral" && "text-muted-foreground")}>
            {trend.direction === "up" && "\u2191"}{trend.direction === "down" && "\u2193"}{trend.direction === "neutral" && "\u2192"}{trend.value}
          </span>
        )}
      </div>
      <div className={cn("flex h-14 w-14 items-center justify-center rounded-xl", iconBgMap[iconVariant])}>{icon}</div>
    </div>
  ),
);
KpiCard.displayName = "KpiCard";

export { KpiCard };
